import * as authService from './authService';
import {
  API_KEY,
  CLIENT_APP,
  CLIENT_NAME,
  IDENTITY_SERVICE_BASE_URL,
  PROFILE_SERVICE_BASE_URL,
} from './clientConfig';

const PROFILE_API_BASE = PROFILE_SERVICE_BASE_URL;
const IDENTITY_API_BASE = IDENTITY_SERVICE_BASE_URL;

async function buildHeaders(token: string): Promise<Record<string, string>> {
  const clientId = (await authService.getClientId()) || localStorage.getItem('olitask_client_id') || '';
  const storedUser = authService.getUser();
  const bearer = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: bearer,
    'O-CLIENT-APP': CLIENT_APP,
    'O-API-KEY': API_KEY,
    'O-CLIENT-NAME': CLIENT_NAME,
  };

  if (clientId) headers['O-CLIENT-ID'] = clientId;

  if (storedUser?.id) {
    headers['O-ACTOR-ID'] = storedUser.id;
  }

  return headers;
}

export async function getUserProfile(refresh = true) {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('Not authenticated. Please sign in first.');
  }

  const response = await fetch(`${PROFILE_API_BASE}/api/v1/user-profiles/get?refresh=${refresh}`, {
    headers: await buildHeaders(token),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to fetch user profile');
  }

  return response.json();
}

async function fetchWithRefresh(url: string, init: RequestInit, fallbackMessage: string) {
  let response = await fetch(url, init);

  if ((response.status === 401 || response.status === 403) && authService.getRefreshToken()) {
    const refreshed = await authService.refreshTokens();
    if (refreshed) {
      const freshToken = authService.getAccessToken() || '';
      response = await fetch(url, {
        ...init,
        headers: await buildHeaders(freshToken),
      });
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || fallbackMessage);
  }

  return response.json().catch(() => null);
}

export async function patchProfileFields(changes: Array<{ name: string; value: any }>) {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('Not authenticated. Please sign in first.');
  }

  return fetchWithRefresh(
    `${PROFILE_API_BASE}/api/v1/user-profiles/props`,
    {
      method: 'PATCH',
      headers: await buildHeaders(token),
      body: JSON.stringify(changes),
    },
    'Failed to update profile'
  );
}

export async function patchIdentityFields(changes: Array<{ name: string; value: any }>) {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('Not authenticated. Please sign in first.');
  }

  return fetchWithRefresh(
    `${IDENTITY_API_BASE}/api/v1/users/props`,
    {
      method: 'PATCH',
      headers: await buildHeaders(token),
      body: JSON.stringify(changes),
    },
    'Failed to update account identity'
  );
}

export async function updateProfileAddress(userId: string, address: Record<string, any>) {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('Not authenticated. Please sign in first.');
  }

  return fetchWithRefresh(
    `${PROFILE_API_BASE}/api/v1/user-profiles/user/${encodeURIComponent(userId)}/address`,
    {
      method: 'POST',
      headers: await buildHeaders(token),
      body: JSON.stringify(address),
    },
    'Failed to update address'
  );
}

export async function submitIdentityVerification(
  userId: string,
  payload: { identificationDocumentType: string; idDocumentIds: string[]; documentType?: string }
) {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('Not authenticated. Please sign in first.');
  }

  return fetchWithRefresh(
    `${PROFILE_API_BASE}/api/v1/user-profiles/identity-verification/user/${encodeURIComponent(userId)}`,
    {
      method: 'POST',
      headers: await buildHeaders(token),
      body: JSON.stringify({
        documentType: payload.documentType || payload.identificationDocumentType,
        identificationDocumentType: payload.identificationDocumentType,
        idDocumentIds: payload.idDocumentIds,
      }),
    },
    'Failed to submit identity verification documents'
  );
}

export async function submitWorkAuthorization(
  userId: string,
  payload: Record<string, any> & { idDocumentIds: string[] }
) {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('Not authenticated. Please sign in first.');
  }

  return fetchWithRefresh(
    `${PROFILE_API_BASE}/api/v1/user-profiles/work-authorization/user/${encodeURIComponent(userId)}`,
    {
      method: 'POST',
      headers: await buildHeaders(token),
      body: JSON.stringify(payload),
    },
    'Failed to submit work authorization documents'
  );
}
